import { isLoading } from "apollo/utils";
import clsx from "clsx";
import {
  FILE_TABLE_ACTION_UPLOAD_AND_SHARE_FORM,
  FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
  FILE_TABLE_ACTION_UPLOAD_HIS,
  TRACK_EVENTS,
} from "core/consts";
import { useExtractDocs } from "core/model/api/endpoints/extractDocs/context";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { FileHISStateful, FileState, QueryProgress } from "core/types";
import {
  Button,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "ds/ui";
import { SetAction } from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import {
  FileFeatureData,
  useFilesFeatures,
} from "dsl/ecosystems/PatientFiles/useFilesFeature";
import { isFileForm } from "dsl/ecosystems/PatientFiles/utils";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { useIsScrollable } from "dsl/hooks";
import { isFileHISStateful } from "dsl/organisms/FileTables";
import { ImportIcon, UploadIcon } from "lucide-react";
import { useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import { FilesTable } from "../FilesTable";
import { ExtractBanner } from "./utils";

export function HISFileUploadScreen({
  availableFiles,
  getFilesHISProgress,
  handleUploadFilesHIS,
  isMessenger,
  mutationProgress,
  setAction,
  unavailableFiles,
}: {
  availableFiles: FileState[] | null;
  handleUploadFilesHIS: ReturnType<
    typeof useFilesFeatures
  >["handleUploadFilesHIS"];
  getFilesHISProgress: QueryProgress;
  mutationProgress: QueryProgress;
  setAction: SetAction<FileFeatureData>;
  unavailableFiles: FileHISStateful[] | null;
  isMessenger?: boolean;
}) {
  const [filesSelected, setFilesSelected] = useState<FileState[]>([]);

  const { isScrollable, ref: modalBodyRef } = useIsScrollable<HTMLDivElement>([
    availableFiles,
    unavailableFiles,
    getFilesHISProgress,
  ]);

  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();
  const { extractOcr } = useExtractDocs();
  const isDocsAndExtractEnabled =
    isDocsAndExtractActive || isDischargeLetterActive;

  const translations = useTranslations();
  const toast = useToast();
  const { trackEvent } = useTracking();

  const methods = useForm({
    defaultValues: { should_extract_on_upload: !isMessenger },
  });

  const shouldExtractOnUpload = useWatch({
    control: methods.control,
    name: "should_extract_on_upload",
  });

  function handleClickSelectFromComputer() {
    trackEvent({
      file_implementation: "HIS",
      modal: FILE_TABLE_ACTION_UPLOAD_HIS,
      name: TRACK_EVENTS.SELECT_FROM_COMPUTER_CLICKED,
    });
    setAction({
      data: {} as FileFeatureData,
      type: FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
    });
  }

  async function handleUploadFile() {
    trackEvent({
      file_implementation: "HIS",
      modal: FILE_TABLE_ACTION_UPLOAD_HIS,
      name: TRACK_EVENTS.UPLOAD_FILE_CLICKED,
    });

    const hisFiles = filesSelected.filter((file): file is FileHISStateful =>
      isFileHISStateful(file),
    );
    const formFiles = filesSelected.filter((file) => isFileForm(file));

    if (formFiles.length > 0) {
      setAction({
        data: formFiles,
        type: FILE_TABLE_ACTION_UPLOAD_AND_SHARE_FORM,
      });
    }

    setAction(undefined);

    if (hisFiles.length > 0) {
      await handleUploadFilesHIS({
        filesHIS: hisFiles,
        onComplete: async ({ category, file, fileId }) => {
          if (
            isDocsAndExtractEnabled &&
            shouldExtractOnUpload &&
            !isMessenger
          ) {
            try {
              await extractOcr.handleOcrForFile({
                file: file,
                fileCategory: category,
                id: fileId,
              });
            } catch {
              toast({ color: "danger", message: translations.general.error });
            }
          }
        },
      });
    }

    setFilesSelected([]);
  }

  return (
    <FormProvider {...methods}>
      <ModalContent>
        <ModalHeader
          as="h1"
          className="border-b border-divider pb-4 text-heading-xl! font-bold"
          id="his-file-upload-screen-title"
        >
          {translations.fileHisUploadComponent.titleNew}
        </ModalHeader>
        <ModalBody className="p-6!">
          <div ref={modalBodyRef} className="flex flex-col gap-6">
            <FilesTable
              data={availableFiles}
              description={
                translations.fileSection.fileUploadComponent
                  .descriptionAvailableFiles
              }
              filesSelected={filesSelected}
              queryProgress={getFilesHISProgress}
              setFilesSelected={setFilesSelected}
              title={
                translations.extractDocs.fileUploadModal.filesAvailableForImport
              }
            />
            <FilesTable
              data={unavailableFiles}
              description={
                translations.fileSection.fileUploadComponent
                  .descriptionUnavailableFiles
              }
              filesSelected={null}
              queryProgress={getFilesHISProgress}
              setFilesSelected={null}
              title={
                translations.fileSection.fileListComponent.titleFilesUnavailable
              }
            />
          </div>
        </ModalBody>
        <ModalFooter
          className={clsx("flex w-full flex-col gap-4 p-6!", {
            "relative shadow-2xl!": isScrollable,
          })}
        >
          {!!availableFiles?.length && !isMessenger && <ExtractBanner />}
          <div className="flex flex-row justify-between gap-4">
            <Button
              color="primary"
              id="select_file_from_computer"
              size="lg"
              startContent={<UploadIcon />}
              variant="ghost"
              onPress={handleClickSelectFromComputer}
            >
              {translations.fileSection.buttonUploadFromComputer}
            </Button>
            <Button
              color="primary"
              id="upload_his"
              isDisabled={!filesSelected.length}
              isLoading={isLoading(mutationProgress)}
              size="lg"
              startContent={<ImportIcon />}
              onPress={handleUploadFile}
            >
              {translations.fileSection.buttonImport}
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </FormProvider>
  );
}
